import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Palette } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const navLinkClasses = (active) =>
  `rounded-full px-4 py-2 text-sm font-medium transition ${
    active
      ? "bg-[var(--pill)] text-[var(--text)]"
      : "text-[var(--text-muted)] hover:text-[var(--text)]"
  }`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const mobileThemeRef = useRef(null);
  const desktopThemeRef = useRef(null);
  const location = useLocation();
  const { theme, setTheme, themes } = useTheme();

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/projects", label: "Projects" },
    { path: "/contact", label: "Contact" },
  ];

  const toggleMenu = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mobileThemeRef.current &&
        !mobileThemeRef.current.contains(e.target) &&
        desktopThemeRef.current &&
        !desktopThemeRef.current.contains(e.target)
      ) {
        setThemeOpen(false);
      }
      if (
        !desktopThemeRef.current &&
        mobileThemeRef.current &&
        !mobileThemeRef.current.contains(e.target)
      ) {
        setThemeOpen(false);
      }
      if (
        !mobileThemeRef.current &&
        desktopThemeRef.current &&
        !desktopThemeRef.current.contains(e.target)
      ) {
        setThemeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      className="fixed left-1/2 top-6 z-50 w-[min(1100px,92vw)] -translate-x-1/2 rounded-full border shadow-[0_24px_45px_rgba(0,0,0,0.45)] backdrop-blur"
      style={{
        backgroundColor: "var(--bg-soft)",
        borderColor: "var(--border)",
      }}
    >
      <div className="flex h-[72px] items-center justify-between gap-6 px-6">
        <Link
          to="/"
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
          onClick={() => setIsOpen(false)}
        >
          <span
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: "var(--accent)" }}
          />
          <div>
            <strong className="text-lg font-semibold">Satya Chamana</strong>
            <span
              className="block text-sm"
              style={{ color: "var(--text-muted)" }}
            >
              AI Engineer · ML · Full-Stack
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-3 md:hidden" ref={mobileThemeRef}>
          <div className="relative">
            <button
  onClick={() => setThemeOpen(!themeOpen)}
  className="relative flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-200"
  style={{
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
    backgroundColor: 'var(--pill)',
    boxShadow: `0 0 12px var(--accent-glow), 0 0 4px var(--accent-glow)`,
    animation: 'pulse-glow 2s ease-in-out infinite',
  }}
  aria-label="Switch theme"
>
  <Palette size={16} />
</button>
            {themeOpen && (
              <div
                className="fixed left-4 right-4 top-20 rounded-2xl border p-3 shadow-xl z-50"
                style={{
                  backgroundColor: "var(--card)",
                  borderColor: "var(--border)",
                }}
              >
                <span
                  className="mb-2 block text-[0.6rem] font-semibold uppercase tracking-widest"
                  style={{ color: "var(--text-muted)" }}
                >
                  Switch Theme
                </span>
                <div className="grid grid-cols-5 gap-1.5">
                  {themes.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setTheme(t.id);
                        setThemeOpen(false);
                      }}
                      className="flex flex-col items-center gap-1 rounded-xl p-2 transition-all"
                      style={{
                        background:
                          theme === t.id ? "var(--pill)" : "transparent",
                        border:
                          theme === t.id
                            ? "1px solid var(--accent)"
                            : "1px solid transparent",
                      }}
                    >
                      <span
                        className="h-5 w-5 rounded-full"
                        style={{
                          backgroundColor: t.color,
                          boxShadow:
                            theme === t.id ? `0 0 8px ${t.color}40` : "none",
                        }}
                      />
                      <span
                        className="text-[0.5rem] font-medium leading-tight text-center"
                        style={{
                          color:
                            theme === t.id
                              ? "var(--text)"
                              : "var(--text-muted)",
                        }}
                      >
                        {t.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            style={{ color: "var(--text)" }}
            onClick={toggleMenu}
            aria-label="Toggle navigation"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <nav
          className={`${
            isOpen ? "scale-y-100" : "scale-y-0 md:scale-y-100"
          } absolute left-0 top-[90px] w-full origin-top rounded-[30px] border px-6 py-5 transition md:static md:flex md:w-auto md:scale-y-100 md:items-center md:gap-5 md:border-0 md:bg-transparent md:p-0`}
          style={{
            backgroundColor: isOpen ? "var(--bg-soft)" : undefined,
            borderColor: "var(--border)",
          }}
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={navLinkClasses(location.pathname === item.path)}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 md:mt-0 md:ml-3 md:justify-end">
            <div className="hidden md:block relative" ref={desktopThemeRef}>
              <button
                onClick={() => setThemeOpen(!themeOpen)}
                className="flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-200 hover:scale-110"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text-muted)",
                  backgroundColor: "var(--pill)",
                  boxShadow: `0 0 12px var(--accent-glow), 0 0 4px var(--accent-glow)`,
    animation: 'pulse-glow 2s ease-in-out infinite',
                }}
                aria-label="Switch theme"
              >
                <Palette size={16} />
              </button>
              {themeOpen && (
                <div
                  className="absolute right-0 top-12 rounded-2xl border p-3 shadow-xl"
                  style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--border)",
                    minWidth: "220px",
                  }}
                >
                  <span
                    className="mb-2 block text-[0.6rem] font-semibold uppercase tracking-widest"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Switch Theme
                  </span>
                  <div className="grid grid-cols-3 gap-1.5">
                    {themes.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => {
                          setTheme(t.id);
                          setThemeOpen(false);
                        }}
                        className="flex flex-col items-center gap-1 rounded-xl p-2 transition-all"
                        style={{
                          background:
                            theme === t.id ? "var(--pill)" : "transparent",
                          border:
                            theme === t.id
                              ? "1px solid var(--accent)"
                              : "1px solid transparent",
                        }}
                      >
                        <span
                          className="h-5 w-5 rounded-full"
                          style={{
                            backgroundColor: t.color,
                            boxShadow:
                              theme === t.id ? `0 0 8px ${t.color}40` : "none",
                          }}
                        />
                        <span
                          className="text-[0.55rem] font-medium"
                          style={{
                            color:
                              theme === t.id
                                ? "var(--text)"
                                : "var(--text-muted)",
                          }}
                        >
                          {t.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold"
              style={{
                backgroundColor: "var(--accent)",
                color: "var(--accent-foreground)",
              }}
              href="https://calendly.com/satyajason143/30min"
              target="_blank"
              rel="noopener noreferrer"
            >
              Let's Talk
              <ArrowUpRight size={16} />
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
